import { type GraphQLFormattedError } from 'graphql';

const VALIDATION_ERROR_FIELD_AND_MESSAGE_PATTERN = /^(.+): (.+)$/;

type BaseValidationErrorFields = object;
export class ValidationError<Fields extends BaseValidationErrorFields> {
  field: RecursiveKeyOf<Fields>;
  message: string;

  static parseGraphQLError<Fields extends BaseValidationErrorFields>(
    error: GraphQLFormattedError,
  ): ValidationError<Fields> | undefined {
    const errorParts = VALIDATION_ERROR_FIELD_AND_MESSAGE_PATTERN.exec(
      (error.extensions?.message ?? '') as string,
    );

    if (errorParts == null) {
      return;
    }

    const sanitizedField = (errorParts[1] ?? '').trim() as Optional<
      RecursiveKeyOf<Fields>
    >;
    const sanitizedMessage = (errorParts[2] ?? '').trim();

    if (!sanitizedField || !sanitizedMessage) {
      return;
    }

    return new ValidationError<Fields>({
      field: sanitizedField,
      message: sanitizedMessage,
    });
  }

  constructor({
    field,
    message,
  }: {
    field: RecursiveKeyOf<Fields>;
    message: string;
  }) {
    this.field = field;
    this.message = message;
  }
}

export class ValidationErrorsHash<Fields extends BaseValidationErrorFields> {
  errors: Partial<Record<RecursiveKeyOf<Fields>, Set<string>>>;

  static build<Fields extends BaseValidationErrorFields>(
    errors?: readonly GraphQLFormattedError[],
  ): ValidationErrorsHash<Fields> {
    return (errors ?? []).reduce((validationErrorsHash, error) => {
      const validationError = ValidationError.parseGraphQLError<Fields>(error);
      if (validationError) validationErrorsHash.addError(validationError);
      return validationErrorsHash;
    }, new ValidationErrorsHash<Fields>());
  }

  constructor() {
    this.errors = {};
  }

  addError({
    field,
    message,
  }: ValidationError<Fields>): ValidationErrorsHash<Fields> {
    this.errors[field] = this.errors[field] || new Set();
    this.errors[field]?.add(message);
    return this;
  }

  isEmpty(): boolean {
    return Object.keys(this.errors).length === 0;
  }

  toString(): string {
    return JSON.stringify(this.errors);
  }
}
